import React, { useState, useEffect } from "react";
import Login from "./authentication/login/Login";
import NavMenu from "./nav-menu/NavMenu";
import ChatsPanel from "./chats-panel/ChatsPanel";
import { Route, Routes, useLocation } from "react-router-dom";
import { GuardedRoute } from "./shared/components/guarded-route/GuardedRoute";
import ContactMain from "./contact/contact-main/ContactMain";
import ContactItemInfo from "./contact/contact-main/contact-list/contact-item/contact-item-info/ContactItemInfo";
import OperatorsMain from "./operator/operators-main/OperatorsMain";
import ChannelMain from "./channel/channel-main/ChannelMain";
import ChatsMain from "./chats/chats-main/ChatsMain";
import SettingsMain from "./setting/settings-main/SettingsMain";
import MailingMain from "./mailing/mailing-main/MailingMain";
import MailingItemInfo from "./mailing/mailing-main/mailing-list/mailing-item-info/MailingItemInfo";
import IUser from "./authentication/~store/models/User";
import LicenseActivation from "./license/license-activation/LicenseActivation";
import LicenseManage from "./license/license-manage/LicenseMange";
import AnalyticsMain from "./analytics/analytics-main/AnalyticsMain";
import LicenseExpiresDialog from "./chat/license-expires-dialog/LicenseExpiresDialog";

import { SplitterLayout } from "./shared/components/splitter-layout";
import "./shared/components/splitter-layout/SplitterLayout.css";
import "./App.css";
import TopicEditor from "./nlp/topic-editor/TopicEditor";
import KnowledgeBaseMain from "./nlp/knowledge-base-main/KnowledgeBaseMain";
import { PermissionType } from "./shared/components/user-context/models/PermissionType";
import Chat from "./chat/Chat";
import TopicItemSearch from "./nlp/topic-list/topic-item-search/TopicItemSearch";
import PermissionMain from "./permission/permission-main/PermissionMain";
import "./shared/localization/i18n";
import useKeyboardNavigation from "./shared/hooks/useKeyboardNavigation";
import MailingRecipientsList from "./mailing/mailing-main/mailing-list/mailing-recipients/MailingRecipientsList";
import AuditMain from "./audit/audit-main/AuditMain";
import { useDispatch } from "react-redux";
import { getGlobalSettings } from "./setting/~store/actions/actions";
import { NLP_EDIT_TOPIC_PATH, NLP_PATH, NLP_SEARCH_TOPIC_PATH } from "./AppConstants";

const App = () => {
  const dispatch = useDispatch();
  const [isChatVisible, setChatVisibility] = useState(false);
  const [isProfileDropdownVisible, toggleProfileDropDownVisibility] =
    useState(false);
  const [isChatWideMode, setIsChatWideMode] = useState(false);
  const [isSidebarsVisible, setIsSidebarsVisible] = useState(checkIsAuth());

  const _setChatVisibility = (isChatVisible: boolean) => {
    setChatVisibility(isChatVisible);
  };

  const _getSwitch = () => {
    return (
      <>
        {isSidebarsVisible && <LicenseExpiresDialog />}
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/login/:message" element={<Login />} />
          <Route path="/activate-access" element={<LicenseActivation />} />

          <Route
            path="/contacts/edit-contact/:contactId"
            element={
              <GuardedRoute
                component={ContactItemInfo}
                permissions={[
                  PermissionType.Chats_ManageOwnChats,
                  PermissionType.Chats_ManageOthersChats,
                ]}
              />
            }
          />
          <Route
            path={`${NLP_EDIT_TOPIC_PATH}/:topicExternalId`}
            element={
              <GuardedRoute
                component={TopicEditor}
                permissions={[PermissionType.NLP_Manage]}
              />
            }
          />
          <Route
            path="/mailing/edit-mailing/:mailingId"
            element={
              <GuardedRoute
                component={MailingItemInfo}
                permissions={[PermissionType.Mailing_Manage]}
              />
            }
          />
          <Route
            path="/mailing/mailing-recipients/:mailingId"
            element={
              <GuardedRoute
                component={MailingRecipientsList}
                setChatVisibility={_setChatVisibility}
                permissions={[PermissionType.Mailing_Manage]}
              />
            }
          />
          <Route
            path="/mailing"
            element={
              <GuardedRoute
                component={MailingMain}
                permissions={[PermissionType.Mailing_Manage]}
              />
            }
          />
          <Route
            path="/contacts"
            element={
              <GuardedRoute
                component={ContactMain}
                permissions={[PermissionType.Contacts_Manage]}
              />
            }
          />
          <Route
            path="/operators"
            element={
              <GuardedRoute
                component={OperatorsMain}
                permissions={[PermissionType.Operators_Manage]}
              />
            }
          />
          <Route
            path="/channels"
            element={
              <GuardedRoute
                component={ChannelMain}
                permissions={[PermissionType.Channels_Manage]}
              />
            }
          />
          <Route
            path={`${NLP_SEARCH_TOPIC_PATH}/:topicName`}
            element={
              <GuardedRoute
                component={TopicItemSearch}
                permissions={[PermissionType.NLP_Search]}
              />
            }
          />
          <Route
            path={NLP_PATH}
            element={
              <GuardedRoute
                component={KnowledgeBaseMain}
                permissions={[
                  PermissionType.NLP_Manage,
                  PermissionType.NLP_Search,
                ]}
              />
            }
          />
          <Route
            path="/chats"
            element={
              <GuardedRoute
                component={ChatsMain}
                setChatVisibility={_setChatVisibility}
                permissions={[
                  PermissionType.Chats_ManageOwnChats,
                  PermissionType.Chats_ManageOthersChats,
                ]}
              />
            }
          >
            <Route
              path=":filter"
              element={
                <GuardedRoute
                  component={ChatsMain}
                  setChatVisibility={_setChatVisibility}
                  permissions={[
                    PermissionType.Chats_ManageOwnChats,
                    PermissionType.Chats_ManageOthersChats,
                  ]}
                />
              }
            />
          </Route>
          <Route
            path="/settings"
            element={
              <GuardedRoute
                component={SettingsMain}
                permissions={[PermissionType.Integrations_Manage]}
              />
            }
          />
          <Route
            path="/permission"
            element={
              <GuardedRoute
                component={PermissionMain}
                permissions={[PermissionType.Permission_Manage]}
              />
            }
          />
          <Route
            path="/audit"
            element={
              <GuardedRoute
                component={AuditMain}
                permissions={[PermissionType.Audit_Manage]}
              />
            }
          />
          <Route
            path="/analytics"
            element={
              <GuardedRoute
                component={AnalyticsMain}
                permissions={[PermissionType.CanManageAnalytics]}
              />
            }
          />
          <Route
            path="/license"
            element={
              <GuardedRoute
                component={LicenseManage}
                permissions={[PermissionType.License_Manage]}
              />
            }
          />
          <Route
            path="/"
            element={
              <GuardedRoute
                component={ChatsMain}
                setChatVisibility={_setChatVisibility}
                permissions={[
                  PermissionType.Chats_ManageOwnChats,
                  PermissionType.Chats_ManageOthersChats,
                ]}
              />
            }
          />
        </Routes>
      </>
    );
  };

  const location = useLocation();

  useEffect(() => {
    location.pathname.startsWith("/chats")
      ? setIsChatWideMode(true)
          : setIsChatWideMode(false);
    location.pathname.startsWith("/login")
      ? setIsSidebarsVisible(false)
          : setIsSidebarsVisible(checkIsAuth());
  }, [location.pathname]);

  useEffect(() => {
    if (isSidebarsVisible) dispatch(getGlobalSettings() as any);
  }, [dispatch, isSidebarsVisible]);

  useKeyboardNavigation();

  if (Notification.permission === "default") Notification.requestPermission();

  return (
    <div className="app-container">
      <div className="app__navmenu">{isSidebarsVisible && <NavMenu />}</div>
      <div className="app__router">
        <SplitterLayout
          customClassName="app__router--split-pane"
          primaryIndex={1}
          percentage={true}
          primaryMinSize={30}
          secondaryMinSize={30}
          secondaryInitialSize={50}
        >
          {_getSwitch()}
          {isChatVisible && (
            <Chat
              isWideMode={isChatWideMode}
              setChatVisibility={setChatVisibility}
            />
          )}
        </SplitterLayout>
      </div>
      <div className="app__chats-panel">
        {isSidebarsVisible && (
          <ChatsPanel
            setChatVisibility={_setChatVisibility}
            isChatVisible={isChatVisible}
            toggleProfileDropDownVisibility={toggleProfileDropDownVisibility}
            isProfileDropdownVisible={isProfileDropdownVisible}
            isWideMode={isChatWideMode}
          />
        )}
      </div>
    </div>
  );
};

const checkIsAuth = (): boolean => {
  try {
    const user: IUser = JSON.parse(sessionStorage.getItem("user") ?? "");
    if (user.isAuth) {
      return true;
    } else {
      return false;
    }
  } catch (ex) {
    return false;
  }
};

export default App;
